import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Bimbi from "@/pages/Bimbi";
import Diario from "@/pages/Diario";
import Presenze from "@/pages/Presenze";
import Staff from "@/pages/Staff";
import Incassi from "@/pages/Incassi";
import Fatture from "@/pages/Fatture";
import PrimaNota from "@/pages/PrimaNota";
import Impostazioni from "@/pages/Impostazioni";
import ParentPortal from "@/pages/ParentPortal";
import Comunicazioni from "@/pages/Comunicazioni";
import Modulistica from "@/pages/Modulistica";
import Deleghe from "@/pages/Deleghe";
import DocumentiGenitori from "@/pages/DocumentiGenitori";
import BustePagaMaestra from "@/pages/BustePagaMaestra";
import SectionPortal from "@/pages/SectionPortal";
import { Baby } from "lucide-react";
import { useEffect } from "react";
import { api, API } from "@/lib/api";

function BrandingSync() {
  useEffect(() => { api.get("/branding").then(({ data }) => {
    document.title = data.nome_azienda || "APiccoliPassi";
    document.documentElement.style.setProperty("--brand-primary", data.colore_primario);
    document.documentElement.style.setProperty("--brand-secondary", data.colore_secondario);
    document.documentElement.style.setProperty("--brand-background", data.colore_sfondo);
    if (data.has_logo) {
      const logoUrl = `${API}/branding/logo?v=${Date.now()}`;
      let icon = document.querySelector("link[rel='icon']");
      if (!icon) { icon = document.createElement("link"); icon.rel = "icon"; document.head.appendChild(icon); }
      icon.href = logoUrl;
      let appleIcon = document.querySelector("link[rel='apple-touch-icon']");
      if (!appleIcon) { appleIcon = document.createElement("link"); appleIcon.rel = "apple-touch-icon"; document.head.appendChild(appleIcon); }
      appleIcon.href = logoUrl;
      const currentPath = window.location.pathname;
      const isParentPortal = currentPath.startsWith("/genitori");
      const sectionMatch = currentPath.match(/^\/sezione\/([^/]+)/);
      let manifestLink = document.querySelector("link[rel='manifest']");
      if (!manifestLink) { manifestLink = document.createElement("link"); manifestLink.rel = "manifest"; document.head.appendChild(manifestLink); }
      manifestLink.href = sectionMatch
        ? `${API}/branding/manifest.webmanifest?portal=section&token=${encodeURIComponent(sectionMatch[1])}`
        : `${API}/branding/manifest.webmanifest?portal=${isParentPortal ? "parent" : "staff"}`;
    }
  }).catch(() => {}); }, []);
  return null;
}

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF6F0]" data-testid="app-loading">
      <div className="flex flex-col items-center gap-3 animate-pulse">
        <div className="w-14 h-14 rounded-2xl bg-[#E07A5F]/20" aria-hidden="true" />
        <p className="font-heading text-xl text-[#3D405B]">APiccoliPassi</p>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" state={{ from: location }} replace />;
  return <Layout>{children}</Layout>;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/presenze" replace />;
  return <Layout>{children}</Layout>;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to={user.role === "maestra" ? "/presenze" : "/"} replace />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrandingSync />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/genitori" element={<ParentPortal />} />
            <Route path="/sezione/:token" element={<SectionPortal />} />
            <Route path="/" element={<AdminOnly><Dashboard /></AdminOnly>} />
            <Route path="/bimbi" element={<AdminOnly><Bimbi /></AdminOnly>} />
            <Route path="/diario" element={<Protected><Diario /></Protected>} />
            <Route path="/presenze" element={<Protected><Presenze /></Protected>} />
            <Route path="/comunicazioni" element={<Protected><Comunicazioni /></Protected>} />
            <Route path="/documenti-genitori" element={<Protected><DocumentiGenitori /></Protected>} />
            <Route path="/buste-paga-maestra" element={<Protected><BustePagaMaestra /></Protected>} />
            <Route path="/staff" element={<AdminOnly><Staff /></AdminOnly>} />
            <Route path="/incassi" element={<AdminOnly><Incassi /></AdminOnly>} />
            <Route path="/fatture" element={<AdminOnly><Fatture /></AdminOnly>} />
            <Route path="/prima-nota" element={<AdminOnly><PrimaNota /></AdminOnly>} />
            <Route path="/modulistica" element={<AdminOnly><Modulistica /></AdminOnly>} />
            <Route path="/deleghe" element={<Protected><Deleghe /></Protected>} />
            <Route path="/impostazioni" element={<AdminOnly><Impostazioni /></AdminOnly>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
